import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';

import type { IconName } from '#ui/MaterialIcon.js';

import { formatDate, formatDateRange } from '@elara/client-core/lib/date-utils';

import { IconSizes, MaterialIcon } from '#ui/index.js';

interface EventMessageCardProps {
  msg: ChatMessage;
}

interface EventMetadata {
  event?: string;
  eventData?: Record<string, unknown>;
}

const EVENT_ICONS: Record<string, IconName> = { 'trip-dates-updated': 'edit_calendar', 'trip-renamed': 'edit' };

const DEFAULT_EVENT_ICON: IconName = 'info';

function formatEventLabel(
  eventType: string | undefined,
  eventData: Record<string, unknown> | undefined,
): string | undefined {
  if (eventType === 'trip-dates-updated' && eventData) {
    const { startDate, endDate } = eventData as { startDate?: string | null; endDate?: string | null };
    if (startDate && endDate) {
      return `Trip dates updated to ${formatDateRange(startDate, endDate)}`;
    }
    if (startDate) return `Trip start date updated to ${formatDate(startDate)}`;
    if (endDate) return `Trip end date updated to ${formatDate(endDate)}`;
    return 'Trip dates cleared';
  }
  return undefined;
}

/**
 * Renders an event message — a user action performed via the UI rather than
 * typed into the chat (e.g. changing trip dates). Displayed as a centered,
 * muted inline notice instead of a chat bubble.
 */
export function EventMessageCard({ msg }: EventMessageCardProps) {
  const meta = msg.metadata as EventMetadata | undefined;
  const eventType = typeof meta?.event === 'string' ? meta.event : undefined;
  const eventData = meta?.eventData as Record<string, unknown> | undefined;
  const label = formatEventLabel(eventType, eventData) ?? msg.textContent;
  const icon = (eventType ? EVENT_ICONS[eventType] : undefined) ?? DEFAULT_EVENT_ICON;

  return (
    <div className="flex items-center justify-end text-right gap-2 text-sm text-muted-foreground w-full">
      <MaterialIcon icon={icon} size={IconSizes.xs} />
      <span className="truncate">{label}</span>
    </div>
  );
}
